import {
  getSPDataWithParams,
  postSPDataWithoutParams,
  postSPFormDataWithoutParams,
} from './services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const BASE_PATH = '/api/secure/cc/support/contact-actions'

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

function buildPayload(context = {}) {
  return {
    patientId: context?.patientId || undefined,
    practitionerId: context?.practitionerId || undefined,
    bookedEventId: context?.bookedEventId || undefined,
    clinicBrandId: context?.clinicBrandId || undefined,
    clinicBrandName: context?.clinicBrandName || undefined,
    clinicId: context?.clinicId || undefined,
    targetRole: context?.targetRole || undefined,
    eventType: context?.eventType || undefined,
    eventTypesCsv: context?.eventTypesCsv || undefined,
  }
}

function shapeCapabilitiesResult(data, rawResponse) {
  return {
    ok: true,
    data,
    channels: data?.channels || {},
    targetRole: data?.targetRole,
    targetDisplayName: data?.targetDisplayName,
    targetPhoneNumber: data?.targetPhoneNumber,
    targetWhatsappNumber: data?.targetWhatsappNumber,
    targetEmail: data?.targetEmail,
    targetUserId: data?.targetUserId,
    targetActorId: data?.targetActorId,
    clinicBrandName: data?.clinicBrandName,
    sharedWhatsappPatientIds: data?.sharedWhatsappPatientIds,
    resolvedEventTypes: data?.resolvedEventTypes || [],
    rawResponse,
  }
}

async function fetchSupportAgentContactCapabilities(context = {}) {
  const response = await getSPDataWithParams(`${BASE_PATH}/capabilities`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return shapeCapabilitiesResult(getDataMapFromCommonResponse(response), response)
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load support contact capabilities.'),
    rawResponse: response,
  }
}

// The support booked-events listing mounts one contact strip per card, and the list remounts
// wholesale on every tab/filter/sort change and after every booking action. Without this layer
// that meant one GET /capabilities per card per remount (the "capabilities storm"). Three
// defenses, mirroring the mentor portal's practitionerContactService fix:
//   1. results are cached per context for a short TTL,
//   2. identical in-flight look-ups share one promise,
//   3. look-ups mounting in the same tick are coalesced into one POST /capabilities/batch
//      (falls back to per-context GETs until the batch endpoint is deployed).
const capabilitiesCache = new Map()
// The booked-events list re-renders every 60s (a currentTime tick refreshes the live
// "In Progress for N mins" labels), and that re-render remounts each card's contact strip.
// Patient reachability (WhatsApp/email/phone route) does not change minute-to-minute, so a
// TTL shorter than the 60s remount cadence just made every remount miss the cache and re-hit
// POST /capabilities/batch. A 5-minute TTL lets those remounts resolve from cache instead;
// real changes still refresh immediately because sends call refreshCapabilities({forceRefresh}).
const CAPABILITIES_TTL_MS = 300000
const CAPABILITIES_BATCH_WINDOW_MS = 25
const CAPABILITIES_BATCH_MAX = 50
let capabilitiesBatchQueue = []
let capabilitiesBatchTimer = null
let capabilitiesBatchEndpointUnavailable = false

function buildCapabilitiesCacheKey(context = {}) {
  return [
    context?.patientId || '',
    context?.practitionerId || '',
    context?.bookedEventId || '',
    context?.clinicBrandId || '',
    context?.clinicId || '',
    context?.targetRole || '',
    context?.eventType || '',
    context?.eventTypesCsv || '',
  ].join('|')
}

export function clearSupportContactCapabilitiesCache() {
  capabilitiesCache.clear()
}

function isEndpointMissingResponse(rawResponse) {
  const status = rawResponse?.response?.status || rawResponse?.status
  return status === 404 || status === 405 || status === 501
}

async function flushCapabilitiesBatchQueue() {
  const queue = capabilitiesBatchQueue
  capabilitiesBatchQueue = []
  capabilitiesBatchTimer = null
  if (queue.length === 0) {
    return
  }

  const resolveIndividually = async () => {
    await Promise.all(
      queue.map(async ({ context, settle }) => {
        try {
          settle(await fetchSupportAgentContactCapabilities(context))
        } catch (error) {
          settle({ ok: false, message: 'Unable to load support contact capabilities.' })
        }
      }),
    )
  }

  if (queue.length === 1 || capabilitiesBatchEndpointUnavailable) {
    await resolveIndividually()
    return
  }

  let response = null
  try {
    response = await postSPDataWithoutParams(`${BASE_PATH}/capabilities/batch`, {
      requests: queue.map(({ context, key }) => ({ ...buildPayload(context), requestKey: key })),
    })
  } catch (error) {
    response = null
  }

  if (!(response?.status === 200 && isCommonResponseSuccessfulV2(response))) {
    if (isEndpointMissingResponse(response)) {
      // Old backend without the batch endpoint — stop trying for this session.
      capabilitiesBatchEndpointUnavailable = true
    }
    await resolveIndividually()
    return
  }

  const results = getDataMapFromCommonResponse(response)?.results || []
  const resultsByKey = new Map()
  results.forEach((item) => {
    if (item?.requestKey) {
      resultsByKey.set(item.requestKey, item)
    }
  })

  const unresolved = []
  queue.forEach((entry) => {
    const item = resultsByKey.get(entry.key)
    if (!item) {
      unresolved.push(entry)
      return
    }
    if (item.ok) {
      entry.settle(shapeCapabilitiesResult(item.data, response))
    } else {
      entry.settle({
        ok: false,
        message: item.message || 'Unable to load support contact capabilities.',
        rawResponse: response,
      })
    }
  })

  if (unresolved.length > 0) {
    await Promise.all(
      unresolved.map(async ({ context, settle }) => {
        try {
          settle(await fetchSupportAgentContactCapabilities(context))
        } catch (error) {
          settle({ ok: false, message: 'Unable to load support contact capabilities.' })
        }
      }),
    )
  }
}

function enqueueCapabilitiesLookup(context, key) {
  return new Promise((resolve) => {
    capabilitiesBatchQueue.push({ context, key, settle: resolve })
    if (capabilitiesBatchQueue.length >= CAPABILITIES_BATCH_MAX) {
      if (capabilitiesBatchTimer) {
        clearTimeout(capabilitiesBatchTimer)
      }
      void flushCapabilitiesBatchQueue()
      return
    }
    if (!capabilitiesBatchTimer) {
      capabilitiesBatchTimer = setTimeout(() => {
        void flushCapabilitiesBatchQueue()
      }, CAPABILITIES_BATCH_WINDOW_MS)
    }
  })
}

export async function getSupportAgentContactCapabilities(context = {}, { forceRefresh = false } = {}) {
  const cacheKey = buildCapabilitiesCacheKey(context)
  const cached = capabilitiesCache.get(cacheKey)
  if (cached) {
    // Always share an in-flight request; otherwise serve fresh-enough results unless the
    // caller forces a refresh (e.g. after a send, when warm-window state changes server-side).
    if (cached.pending) {
      return cached.promise
    }
    if (!forceRefresh && Date.now() - cached.at < CAPABILITIES_TTL_MS) {
      return cached.promise
    }
    capabilitiesCache.delete(cacheKey)
  }

  const entry = { pending: true, at: Date.now(), promise: null }
  entry.promise = (async () => {
    const result = await enqueueCapabilitiesLookup(context, cacheKey)
    entry.pending = false
    entry.at = Date.now()
    // Don't cache failures — a transient error should self-heal on the next mount.
    if (!result?.ok) {
      capabilitiesCache.delete(cacheKey)
    }
    return result
  })()
  capabilitiesCache.set(cacheKey, entry)
  return entry.promise
}

export async function sendSupportAgentWhatsappIntro(context = {}) {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/whatsapp/intro`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp intro.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentGoogleReview(context = {}) {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/send-google-review`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      reviewLink: data?.reviewLink || '',
      whatsappSent: !!data?.whatsappSent,
      emailSent: !!data?.emailSent,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the Google review link.'),
    rawResponse: response,
  }
}

export async function getSupportAgentWhatsappThread(context = {}) {
  const response = await getSPDataWithParams(`${BASE_PATH}/whatsapp/thread`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response)
    return {
      ok: true,
      data,
      messages: data?.messages || [],
      templateActions: data?.templateActions || [],
      channels: data?.channels || {},
      freeFormAllowed: !!data?.freeFormAllowed,
      freeFormWindowOpen: !!data?.freeFormWindowOpen,
      freeFormWindowStartAt: data?.freeFormWindowStartAt,
      freeFormWindowEndAt: data?.freeFormWindowEndAt,
      targetDisplayName: data?.targetDisplayName,
      targetPhoneNumber: data?.targetPhoneNumber,
      targetWhatsappNumber: data?.targetWhatsappNumber,
      targetEmail: data?.targetEmail,
      targetUserId: data?.targetUserId,
      targetActorId: data?.targetActorId,
      clinicBrandName: data?.clinicBrandName,
      sharedWhatsappPatientIds: data?.sharedWhatsappPatientIds || [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load the WhatsApp conversation.'),
    rawResponse: response,
  }
}

export async function getSupportAgentWhatsappThreads() {
  const response = await getSPDataWithParams(`${BASE_PATH}/whatsapp/threads`, {})

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response)
    return {
      ok: true,
      data,
      threads: data?.threads || [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load support WhatsApp threads.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentWhatsappTemplate(context = {}, templateAction = '') {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/whatsapp/template`, {
    ...buildPayload(context),
    templateAction,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp template.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentWhatsappMessage(context = {}, message = '') {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/whatsapp/message`, {
    ...buildPayload(context),
    message,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp message.'),
    rawResponse: response,
  }
}

export async function prepareSupportAgentEmail(context = {}) {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/email/prepare`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to prepare the email draft.'),
    rawResponse: response,
  }
}

/**
 * Sends a support-agent-composed email (subject + rich HTML body) server-side over SES, instead of
 * handing off to the desktop mail client. Returns { ok, data } or { ok: false, message }.
 */
export async function sendSupportAgentEmail(context = {}, subject = '', htmlBody = '') {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/email/send`, {
    ...buildPayload(context),
    subject: subject || undefined,
    htmlBody,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the email.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentWhatsappAttachment(context = {}, message = "", files = []) {
  const formData = new FormData()
  const payload = buildPayload(context)

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}` !== "") {
      formData.append(key, value)
    }
  })

  if (message) {
    formData.append("caption", message)
  }

  ;(files || []).forEach((file) => {
    if (file) {
      formData.append("files", file)
    }
  })

  const response = await postSPFormDataWithoutParams(`${BASE_PATH}/whatsapp/attachment`, formData)

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, "Unable to send the WhatsApp attachment."),
    rawResponse: response,
  }
}
